import axios from './axiosConfig';
import { 
  Property, 
  UtilityService, 
  Activity, 
  CostOverview, 
  StatusOverview, 
  CreateUtilityServiceDTO,
  EnhancedDashboardSummary,
  GeographicDistribution,
  SupplierDistribution,
  ClientCompletenessScore,
  ContractExpiryForecast
} from '../types/utility.types';

interface ApiResponse<T> {
  data: T;
}

// Property API calls
export const getProperties = async (clientId: string): Promise<Property[]> => {
  const response = await axios.get<ApiResponse<Property[]>>(`/clients/${clientId}/properties`);
  return response.data.data;
};

export const getProperty = async (clientId: string, id: string): Promise<Property> => {
  const response = await axios.get<ApiResponse<Property>>(`/clients/${clientId}/properties/${id}`);
  return response.data.data;
};

export const createProperty = async (clientId: string, data: Omit<Property, '_id'>): Promise<Property> => {
  const response = await axios.post<ApiResponse<Property>>(`/clients/${clientId}/properties`, data);
  return response.data.data;
};

export const updateProperty = async (clientId: string, id: string, data: Partial<Property>): Promise<Property> => {
  const response = await axios.patch<ApiResponse<Property>>(`/clients/${clientId}/properties/${id}`, data);
  return response.data.data;
};

export const deleteProperty = async (clientId: string, id: string): Promise<void> => {
  await axios.delete(`/clients/${clientId}/properties/${id}`);
};

// Utility Service API calls
export const getAllUtilityServices = async (): Promise<UtilityService[]> => {
  const response = await axios.get<UtilityService[]>('/utility-services');
  return response.data || [];
};

export const getClientUtilityServices = async (clientId: string): Promise<UtilityService[]> => {
  const response = await axios.get<UtilityService[]>(`/utility-services?clientId=${clientId}`);
  return response.data || [];
};

export const getUtilityServices = async (clientId: string, propertyId: string): Promise<UtilityService[]> => {
  const response = await axios.get<UtilityService[]>(
    `/utility-services?clientId=${clientId}&propertyId=${propertyId}`
  );
  return response.data || [];
};

export const getUtilityService = async (id: string): Promise<UtilityService> => {
  const response = await axios.get<UtilityService>(
    `/utility-services/${id}`
  );
  return response.data;
};

export const createUtilityService = async (
  clientId: string,
  propertyId: string,
  data: CreateUtilityServiceDTO
): Promise<UtilityService> => {
  // Filter out empty strings but keep required fields
  const filteredData = Object.entries(data).reduce((acc, [key, value]) => {
    // Always include serviceType and status
    if (key === 'serviceType' || key === 'status' || (value !== undefined && value !== '')) {
      acc[key] = value;
    }
    return acc;
  }, {} as Record<string, any>);

  const requestData = { ...filteredData, clientId, propertyId };
  

  const response = await axios.post<UtilityService>(
    `/utility-services`,
    requestData
  );
  
  return response.data;
};

export const updateUtilityService = async (
  id: string,
  data: Partial<UtilityService>
): Promise<UtilityService> => {
  const response = await axios.put<UtilityService>(
    `/utility-services/${id}`,
    data
  );
  return response.data;
};

export const deleteUtilityService = async (
  clientId: string,
  propertyId: string,
  id: string
): Promise<void> => {
  await axios.delete(`/utility-services/${id}?clientId=${clientId}&propertyId=${propertyId}`);
};

export const updateMeterReading = async (
  clientId: string,
  propertyId: string,
  id: string,
  reading: number
): Promise<UtilityService> => {
  const response = await axios.post<ApiResponse<UtilityService>>(
    `/utility-services/${id}/meter-reading`,
    { reading, clientId, propertyId }
  );
  return response.data.data;
};

export const updateReminderSettings = async (
  clientId: string,
  propertyId: string,
  id: string,
  settings: any
): Promise<UtilityService> => {
  const response = await axios.patch<ApiResponse<UtilityService>>(
    `/utility-services/${id}/reminder-settings`,
    { ...settings, clientId, propertyId }
  );
  return response.data.data;
};

// Dashboard API calls
export const getEnhancedDashboardSummary = async (params?: Record<string, string>): Promise<EnhancedDashboardSummary> => {
  try {
    const response = await axios.get<EnhancedDashboardSummary>('/utility-dashboard/summary', { params });
    
    // Debug logging for contractsEndingSoon
    console.log('API Response - contractsEndingSoon:', response.data.contractsEndingSoon);
    if (response.data.contractsEndingSoon && response.data.contractsEndingSoon.length > 0) {
      console.log('First contract from API:', response.data.contractsEndingSoon[0]);
      console.log('First contract renewalStatus from API:', response.data.contractsEndingSoon[0].renewalStatus);
    }
    
    return response.data;
  } catch (error) {
    console.error('Error fetching enhanced dashboard summary:', error);
    throw error;
  }
};

// Legacy method for backward compatibility
export const getDashboardSummary = getEnhancedDashboardSummary;

export const getRecentActivities = async (params?: Record<string, string>): Promise<Activity[]> => {
  try {
    const response = await axios.get<Activity[]>('/utility-dashboard/activities', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching recent activities:', error);
    throw error;
  }
};

export const getGeographicDistribution = async (params?: Record<string, string>): Promise<GeographicDistribution[]> => {
  try {
    const response = await axios.get<GeographicDistribution[]>('/utility-dashboard/geographic-distribution', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching geographic distribution:', error);
    throw error;
  }
};

export const getSupplierDistribution = async (params?: Record<string, string>): Promise<SupplierDistribution[]> => {
  try {
    const response = await axios.get<SupplierDistribution[]>('/utility-dashboard/supplier-distribution', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier distribution:', error);
    throw error;
  }
};

export const getClientCompletenessScores = async (params?: Record<string, string>): Promise<ClientCompletenessScore[]> => {
  try {
    const response = await axios.get<ClientCompletenessScore[]>('/utility-dashboard/client-completeness', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching client completeness scores:', error);
    throw error;
  }
};

export const getContractExpiryForecast = async (params?: Record<string, string>): Promise<ContractExpiryForecast[]> => {
  try {
    const response = await axios.get<ContractExpiryForecast[]>('/utility-dashboard/contract-expiry-forecast', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching contract expiry forecast:', error);
    throw error;
  }
};

export const getCostsOverview = async (params?: Record<string, string>): Promise<CostOverview[]> => {
  try {
    const response = await axios.get<CostOverview[]>('/utility-dashboard/costs', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching costs overview:', error);
    throw error;
  }
};

export const getStatusOverview = async (params?: Record<string, string>): Promise<StatusOverview[]> => {
  try {
    const response = await axios.get<StatusOverview[]>('/utility-dashboard/status', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching status overview:', error);
    throw error;
  }
};

export const getClientRevenueAnalysis = async (params?: Record<string, string>): Promise<any[]> => {
  try {
    const response = await axios.get<any[]>('/utility-dashboard/client-revenue', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching client revenue analysis:', error);
    throw error;
  }
};

export const getServicePenetrationAnalysis = async (params?: Record<string, string>) => {
  try {
    const response = await axios.get('/utility-dashboard/service-penetration', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching service penetration analysis:', error);
    throw error;
  }
};

export const getDataQualityMetrics = async () => {
  const response = await axios.get('/utility-dashboard/data-quality');
  return response.data;
};

export const getContractValueDistribution = async (params?: Record<string, string>): Promise<any[]> => {
  try {
    const response = await axios.get<{ data: any[] }>('/utility-dashboard/contract-value-distribution', { params });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching contract value distribution:', error);
    return [];
  }
};

export const getActiveUnits = async (params?: Record<string, string>): Promise<{ eaq: number; aq: number; breakdown: any[] }> => {
  try {
    const response = await axios.get('/utility-dashboard/active-units', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching active units:', error);
    return { eaq: 0, aq: 0, breakdown: [] };
  }
};
